import { sessionLogout, storeAPIError } from "@/data/store/actions";
import { getAPIErrors } from "@/data/store/selectors/global";
import { useAppDispatch } from "@/data/store/utils/redux";
import { ErrorData } from "@/data/types";
import useNotify from "@/hooks/useNotify";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const APIErrorProvider = ({ children }: { children: React.ReactNode }) => {
  const { showToast } = useNotify();
  const dispatch = useAppDispatch();

  const APIErrors = useSelector(getAPIErrors);

  const { t } = useTranslation("errors");

  useEffect(() => {
    if (!APIErrors) return;

    // A transport failure (offline/timeout/DNS/CORS) has no response body, so
    // the network layer rejects with `undefined` → the string "undefined"
    // reaches us here. An unguarded parse throws inside the effect and takes
    // the toast with it. Treat an unreadable payload as a connectivity error.
    let error: ErrorData | null = null;
    try {
      error = JSON.parse(APIErrors);
    } catch {
      error = null;
    }

    if (!error) {
      showToast({
        type: "error",
        title: "Σφάλμα σύνδεσης",
        message: t("NETWORK_ERROR", "Δεν ήταν δυνατή η σύνδεση με τον διακομιστή."),
      });
      dispatch(storeAPIError(null));
      return;
    }

    // An error body without a `message_code` used to return here — no toast,
    // and the stored error left in place, so the next identical failure didn't
    // even re-run this effect. A request that failed then looked exactly like
    // one that did nothing. Say something generic instead, and always clear.
    if (!error.message_code) {
      showToast({
        type: "error",
        title: "Σφάλμα!",
        message: error.message || t("UNKNOWN_ERROR", "Άγνωστο σφάλμα"),
      });
      dispatch(storeAPIError(null));
      return;
    }

    if (
      error.message_code === "AUTH_CUSTOM_TOKEN_EXPIRED" ||
      error.message === "AUTHENTICATION_FAILED"
    ) {
      dispatch(sessionLogout());
      return;
    }

    showToast({
      type: "error",
      title: "Σφάλμα!",
      // The API already writes `message` for the end user, so it beats a
      // missing translation key.
      message: t(error.message_code, error.message || "Άγνωστο σφάλμα"),
    });

    dispatch(storeAPIError(null));
  }, [APIErrors]);

  return children;
};

export default APIErrorProvider;
